import * as React from "react";

type CardOwnProps = { className?: string; children?: React.ReactNode };

export type CardProps =
  | (CardOwnProps & { as?: "div" } & Omit<React.HTMLAttributes<HTMLDivElement>, keyof CardOwnProps>)
  | (CardOwnProps & { as: "details" } & Omit<React.DetailsHTMLAttributes<HTMLDetailsElement>, keyof CardOwnProps>)
  | (CardOwnProps & { as: "section" } & Omit<React.HTMLAttributes<HTMLElement>, keyof CardOwnProps>);

type DivProps = React.HTMLAttributes<HTMLDivElement>;
type HeadingProps = React.HTMLAttributes<HTMLHeadingElement>;
type ParagraphProps = React.HTMLAttributes<HTMLParagraphElement>;

function cx(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(" ");
}

const ROOT_BASE = "rounded-lg border border-border bg-card";

function CardRoot(props: CardProps) {
  const { className, children, ...rest } = props;
  const as = (props as { as?: "div" | "details" | "section" }).as ?? "div";
  const finalClass = cx(ROOT_BASE, className);
  if (as === "details") {
    const { as: _drop, ...detailsRest } = rest as React.DetailsHTMLAttributes<HTMLDetailsElement> & { as?: unknown };
    return <details className={finalClass} {...detailsRest}>{children}</details>;
  }
  if (as === "section") {
    const { as: _drop, ...sectionRest } = rest as React.HTMLAttributes<HTMLElement> & { as?: unknown };
    return <section className={finalClass} {...sectionRest}>{children}</section>;
  }
  const { as: _drop, ...divRest } = rest as React.HTMLAttributes<HTMLDivElement> & { as?: unknown };
  return <div className={finalClass} {...divRest}>{children}</div>;
}

function CardHeader({ className, ...rest }: DivProps) {
  return (
    <div
      className={cx("border-b border-border px-6 py-4", className)}
      {...rest}
    />
  );
}

function CardTitle({ className, ...rest }: HeadingProps) {
  return (
    <h3
      className={cx(
        "text-base font-semibold text-card-foreground",
        className,
      )}
      {...rest}
    />
  );
}

function CardDescription({ className, ...rest }: ParagraphProps) {
  return (
    <p
      className={cx("mt-1 text-sm text-muted-foreground", className)}
      {...rest}
    />
  );
}

function CardBody({ className, ...rest }: DivProps) {
  return <div className={cx("px-6 py-4", className)} {...rest} />;
}

function CardFooter({ className, ...rest }: DivProps) {
  return (
    <div
      className={cx(
        "border-t border-border bg-muted/50 px-6 py-3",
        className,
      )}
      {...rest}
    />
  );
}

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Title: CardTitle,
  Description: CardDescription,
  Body: CardBody,
  Footer: CardFooter,
});
